import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {Alert, Backdrop, Box, Button, CircularProgress, Tooltip} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import * as _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  HazardAIDailyBreakdownQuery,
  HazardAIDailyBreakdownResponse,
  HazardAIDailyBreakdownRow,
} from '../../../interfaces/HazardAIDailyBreakdown';
import {saveFile} from '../../../utils/file';
import DataGrid, {DataGridColumn} from '../../common/DataGrid';
import SpeedSliderSelector from '../../common/SpeedSliderSelector';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import {HeatmapObjectSelector} from '../HazardHeatmap/HeatmapObjectSelector';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export const HazardAIDailyBreakdown = (props?: Props) => {
  const [panel] = usePanel();
  const {filter, setFilter} = usePanelFilter();

  const [fetchedData, setFetchedData] =
    useState<HazardAIDailyBreakdownResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const params = useMemo<HazardAIDailyBreakdownQuery>(
    () => ({
      date_start:
        filter.params?.date_start ??
        props?.value?.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        filter.params?.date_end ??
        props?.value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      safeye_node_id: _.has(filter.params, 'safeye_node_ids')
        ? filter.params?.safeye_node_ids
        : props?.value?.params?.safeye_node_id,
      site_id: _.has(filter.params, 'site_ids')
        ? filter.params?.site_ids
        : props?.value?.params?.site_id,
      speed_min: filter.params?.speed_min || 0,
      speed_max: filter.params?.speed_max || 200,
      object: filter.params?.object ?? undefined,
    }),
    [filter]
  );

  const fetchData = useCallback(
    async (params: HazardAIDailyBreakdownQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/hazard-ai/daily-breakdown`;
        const resp = await API.get<HazardAIDailyBreakdownResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_hazard_daily_bd');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    []
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const resp = await API.get(`${apiBaseUrl}/hazard-ai/daily-breakdown`, {
        params: {...params, export: 'excel'},
        responseType: 'blob',
      });
      saveFile(
        resp.data,
        `hazardai_daily_breakdown_${params.date_start}_${params.date_end}.xlsx`
      );
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_hazard_daily_bd_xls');
      setFetchedErrors(messages);
    }
    setIsExporting(false);
  };

  useEffect(() => {
    if (params) {
      fetchData(params);
      props?.onUpdate?.({...props.value, params});
    }
  }, [params]);

  const rows: HazardAIDailyBreakdownRow[] = fetchedData ?? [];

  const cols: DataGridColumn<HazardAIDailyBreakdownRow>[] = [
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 90,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'id_zone',
      headerName: 'ID',
      width: 80,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => (
        <Box sx={{color: 'success.main'}}>{row.id_zone}</Box>
      ),
    },
    {
      field: 'warning_zone',
      headerName: 'Warning',
      width: 100,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => (
        <Box sx={{color: 'warning.main'}}>{row.warning_zone}</Box>
      ),
    },
    {
      field: 'hazard_zone',
      headerName: 'Hazard',
      width: 100,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => (
        <Box sx={{color: 'error.main'}}>{row.hazard_zone}</Box>
      ),
    },
    {
      field: 'moving',
      headerName: 'Moving',
      width: 90,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => (
        <Box sx={{color: 'info.main'}}>{row.moving}</Box>
      ),
    },
    {
      field: 'active_hours',
      headerName: 'Active Hours',
      width: 110,
      resizeable: true,
      sortable: true,
    },
  ];

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      {/* Filters */}
      <Box display="flex" gap={1} py={1} flexWrap="wrap">
        <Box maxWidth={300} minWidth={180}>
          <SafeyeNodeSelector
            size="small"
            fullWidth
            allOptionDisabled
            value={
              params.safeye_node_id?.length
                ? +params.safeye_node_id[0]
                : null
            }
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    safeye_node_ids: {
                      $set: v != null ? [String(v)] : [],
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400}>
          <DateRangeSelect
            value={[
              dayjs(params.date_start).toDate(),
              dayjs(params.date_end).toDate(),
            ]}
            size="small"
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    date_start: {
                      $set: v?.[0]
                        ? dayjs(v?.[0]).format('YYYY-MM-DD')
                        : undefined,
                    },
                    date_end: {
                      $set: v?.[1]
                        ? dayjs(v?.[1]).format('YYYY-MM-DD')
                        : undefined,
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={300} minWidth={160}>
          <HeatmapObjectSelector
            size="small"
            fullWidth
            value={params.object}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    object: {$set: v ?? undefined},
                  },
                })
              );
            }}
          />
        </Box>
        <Box>
          <SpeedSliderSelector
            min={0}
            max={200}
            lowerValue={Number(params.speed_min)}
            upperValue={Number(params.speed_max)}
            onLowerChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    speed_min: {
                      $set: v as number,
                    },
                  },
                })
              );
            }}
            onUpperChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    speed_max: {
                      $set: v as number,
                    },
                  },
                })
              );
            }}
          />
        </Box>

        <Box ml="auto" display="flex" alignItems="center">
          <Tooltip title="Export to Excel">
            <Button
              size="small"
              variant="outlined"
              disabled={fetchedInProgress || isExporting}
              onClick={handleExport}
            >
              <FileDownloadIcon />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert key={idx} severity="error">
          {error}
        </Alert>
      ))}

      {/* Table */}
      <Box flex={1} overflow="hidden">
        <DataGrid
          rows={rows}
          columns={cols}
          size="small"
          pagination
          loading={fetchedInProgress}
        />
      </Box>
    </Box>
  );
};
